const IP_CAMERA_URL =
  process.env.REACT_APP_IP_CAMERA_URL ||
  'http://192.168.137.165/axis-cgi/mjpg/video.cgi?resolution=1280x720&compression=50&camera=1&fps=15&refreshKey=0';

let patched = false;
let canvas = null;
let animationFrameId = null;
let styleElement = null;

/**
 * Tests whether the IP camera is reachable by attempting to load a single frame.
 */
function testCameraPresence() {
  return new Promise((resolve) => {
    const testImg = new Image();
    testImg.crossOrigin = 'anonymous';
    const timeout = setTimeout(() => {
      testImg.src = '';
      resolve(false);
    }, 3000);

    testImg.onload = () => {
      clearTimeout(timeout);
      resolve(true);
    };
    testImg.onerror = () => {
      clearTimeout(timeout);
      resolve(false);
    };
    testImg.src = IP_CAMERA_URL;
  });
}

/**
 * Creates a hidden canvas, draws the MJPEG stream onto it, and monkey-patches
 * navigator.mediaDevices.getUserMedia so that any video request returns the
 * IP camera stream instead of the built-in webcam.
 */
function applyPatch() {
  if (patched) return;
  patched = true;

  canvas = document.createElement('canvas');
  canvas.width = 1280;
  canvas.height = 720;
  canvas.style.display = 'none';
  document.body.appendChild(canvas);

  const ctx = canvas.getContext('2d');
  const img = new Image();
  img.crossOrigin = 'anonymous';

  function draw() {
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    animationFrameId = requestAnimationFrame(draw);
  }

  img.onload = () => draw();
  img.src = IP_CAMERA_URL;

  // Hide the Zoom "Video Setting..." dropdown so users can't switch away from the IP camera
  styleElement = document.createElement('style');
  styleElement.textContent = 'button[aria-label="Video Setting..."] { display: none !important; }';
  document.head.appendChild(styleElement);

  const originalGetUserMedia =
    navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);

  navigator.mediaDevices.getUserMedia = async (constraints) => {
    if (constraints && constraints.video) {
      console.log('[IPCamera] Redirecting video to IP camera stream');
      const virtualVideoStream = canvas.captureStream(15);

      // If audio was also requested, get it from the real device
      if (constraints.audio) {
        try {
          const realAudioStream = await originalGetUserMedia({
            audio: constraints.audio,
            video: false,
          });
          return new MediaStream([
            ...virtualVideoStream.getVideoTracks(),
            ...realAudioStream.getAudioTracks(),
          ]);
        } catch (e) {
          console.warn('[IPCamera] No microphone access, returning video only.');
        }
      }

      return virtualVideoStream;
    }
    return originalGetUserMedia(constraints);
  };
}

/**
 * Checks for the IP camera and patches getUserMedia if it's reachable.
 * Returns true if the patch was applied, false otherwise.
 * Safe to call multiple times — the patch is applied at most once.
 */
async function initIPCamera() {
  if (patched) return true;

  const available = await testCameraPresence();
  if (available) {
    console.log('[IPCamera] IP camera detected — applying getUserMedia patch');
    applyPatch();
    return true;
  }

  console.log('[IPCamera] IP camera not found — using default webcam');
  return false;
}

/**
 * Cleans up the hidden canvas and stops the rendering loop.
 * Does NOT restore the original getUserMedia (the Zoom client may still hold
 * references to the patched version).
 */
function cleanup() {
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }
  if (canvas && canvas.parentNode) {
    canvas.parentNode.removeChild(canvas);
    canvas = null;
  }
  if (styleElement && styleElement.parentNode) {
    styleElement.parentNode.removeChild(styleElement);
    styleElement = null;
  }
}

const ipCamera = { initIPCamera, cleanup };
export default ipCamera;
